import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '../../context/CartContext';
import { LanguageSwitcher } from './LanguageSwitcher';

const NAV_KEYS = [
  { key: 'nav_home', path: '/' },
  { key: 'nav_shop', path: '/shop' },
  { key: 'nav_blueProtect', path: '/blue-protect' },
  { key: 'nav_evilEnergy', path: '/evil-energy-destroy' },
  { key: 'nav_destroyKit', path: '/evil-energy-destroy-kit' },
  { key: 'nav_consultations', path: '/consultations' },
  { key: 'nav_blog', path: '/blog' },
  { key: 'nav_about', path: '/about' },
  { key: 'nav_contact', path: '/contact' },
];

export const Navbar = () => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { totalItems, openCart } = useCart();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'top-0 bg-[#050010]/95 backdrop-blur-xl border-b border-violet-500/15'
          : 'top-0 bg-transparent'
      }`}
    >
      <nav className="flex items-center justify-between px-6 lg:px-12 h-24">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 shrink-0">
          <div className="w-[72px] h-[72px] flex items-center justify-center shrink-0">
            <img
              src="https://public.readdy.ai/ai/img_res/d94d3c69-0f7c-4d5d-8dec-5bce3249b295.png"
              alt="ZpiritualZ logo"
              className="w-[72px] h-[72px] object-contain"
            />
          </div>
          <span className="text-white font-black text-xl tracking-tight">
            Zpiritual<span className="text-transparent bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text">Z</span>
          </span>
        </Link>

        {/* Desktop nav links */}
        <ul className="hidden lg:flex items-center gap-7">
          {NAV_KEYS.map(link => {
            const isEvil = link.path === '/evil-energy-destroy';
            const isDestroy = link.path === '/evil-energy-destroy-kit';
            const isActive = location.pathname === link.path;
            return (
              <li key={link.path}>
                <Link
                  to={link.path}
                  className={`text-sm font-medium tracking-wide transition-all duration-300 relative group whitespace-nowrap ${
                    isActive
                      ? isEvil ? 'text-red-400' : isDestroy ? 'text-orange-400' : 'text-violet-400'
                      : 'text-white/70 hover:text-white'
                  }`}
                >
                  {t(link.key)}
                  <span className={`absolute -bottom-1 left-0 h-px transition-all duration-300 ${
                    isEvil
                      ? 'bg-gradient-to-r from-red-400 to-orange-400'
                      : isDestroy
                      ? 'bg-gradient-to-r from-orange-400 to-red-400'
                      : 'bg-gradient-to-r from-violet-400 to-fuchsia-400'
                  } ${isActive ? 'w-full' : 'w-0 group-hover:w-full'}`} />
                </Link>
              </li>
            );
          })}
        </ul>

        {/* Right Actions */}
        <div className="flex items-center gap-3">
          {/* Language Switcher — desktop */}
          <div className="hidden lg:block">
            <LanguageSwitcher />
          </div>

          <button
            onClick={openCart}
            className="relative flex items-center justify-center w-10 h-10 text-white/70 hover:text-violet-400 transition-colors cursor-pointer"
            aria-label={t('nav_myOrders')}
          >
            <i className="ri-shopping-bag-line text-xl" />
            {totalItems > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center">
                {totalItems}
              </span>
            )}
          </button>

          <Link
            to="/orders"
            className="hidden lg:flex items-center justify-center w-10 h-10 text-white/60 hover:text-violet-400 transition-colors cursor-pointer"
            aria-label={t('nav_myOrders')}
            title={t('nav_myOrders')}
          >
            <i className="ri-file-list-3-line text-xl" />
          </Link>

          <Link
            to="/consultations"
            className="hidden lg:flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold rounded-full whitespace-nowrap hover:opacity-90 transition-opacity cursor-pointer"
          >
            <i className="ri-calendar-line text-base" />
            {t('nav_bookConsult')}
          </Link>

          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden flex items-center justify-center w-10 h-10 text-white cursor-pointer"
          >
            <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="lg:hidden bg-[#050010]/98 backdrop-blur-xl border-t border-violet-500/10">
          <ul className="flex flex-col py-4 px-6">
            {NAV_KEYS.map(link => (
              <li key={link.path}>
                <Link
                  to={link.path}
                  className={`block py-3 text-sm font-medium transition-colors ${
                    location.pathname === link.path ? 'text-violet-400' : 'text-white/70'
                  }`}
                >
                  {t(link.key)}
                </Link>
              </li>
            ))}
            <li className="pt-4 pb-2 border-t border-violet-500/10">
              <div className="mb-3">
                <LanguageSwitcher />
              </div>
              <div className="flex flex-col gap-2">
                <Link
                  to="/orders"
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-violet-500/30 text-violet-400 text-sm font-medium rounded-full whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-file-list-3-line" />
                  {t('nav_myOrders')}
                </Link>
                <Link
                  to="/consultations"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold rounded-full whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-calendar-line" />
                  {t('nav_bookConsult')}
                </Link>
              </div>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
};
